// src/services/syncService.js
import { 
  collection, 
  doc, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

class SyncService {
  constructor() {
    this.listeners = new Map();
    this.syncQueue = [];
    this.jobberSyncQueue = [];
    this.isOnline = navigator.onLine;
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    this.syncingQuotes = new Set(); // Track quotes currently being synced to prevent infinite loops
    this.syncingSettings = false; // Track if settings are currently being synced to prevent loops
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // Generate unique device ID for conflict resolution
  getDeviceId() {
    let deviceId = localStorage.getItem('squeakit_device_id');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('squeakit_device_id', deviceId);
    }
    return deviceId;
  }

  // Load sync queue from localStorage
  loadSyncQueue(userId) {
    try {
      const queue = localStorage.getItem(`syncQueue_${userId}`);
      this.syncQueue = queue ? JSON.parse(queue) : [];
    } catch (error) {
      console.error('Error loading sync queue:', error);
      this.syncQueue = [];
    }
  }

  // Save sync queue to localStorage
  saveSyncQueue(userId) {
    try {
      localStorage.setItem(`syncQueue_${userId}`, JSON.stringify(this.syncQueue));
    } catch (error) {
      console.error('Error saving sync queue:', error);
    }
  }

  // Add to sync queue for offline operations
  addToSyncQueue(operation) {
    // Load existing queue for this user if not already loaded
    if (this.syncQueue.length === 0 && operation.userId) {
      this.loadSyncQueue(operation.userId);
    }
    
    this.syncQueue.push({
      ...operation,
      timestamp: Date.now(),
      deviceId: this.getDeviceId()
    });
    
    // Persist to localStorage
    if (operation.userId) {
      this.saveSyncQueue(operation.userId);
    }
    
    if (this.isOnline) {
      this.processSyncQueue();
    }
  }

  // Process pending sync operations
  async processSyncQueue() {
    if (!this.isOnline || this.syncQueue.length === 0) return;

    // Sort operations: DELETE operations first, then CREATE, then UPDATE
    const operations = [...this.syncQueue].sort((a, b) => {
      const order = { DELETE: 0, CREATE: 1, UPDATE: 2 };
      return (order[a.type] || 99) - (order[b.type] || 99);
    });
    
    const userId = operations[0]?.userId; // Get userId from first operation
    this.syncQueue = [];
    
    // Save empty queue immediately
    if (userId) {
      this.saveSyncQueue(userId);
    }

    for (const operation of operations) {
      try {
        await this.executeOperation(operation);
        
        // Save queue after successful operation
        if (operation.userId) {
          this.saveSyncQueue(operation.userId);
        }
      } catch (error) {
        console.error('Sync operation failed:', error);
        
        // Handle "not found" errors for UPDATE/DELETE operations - remove from queue
        if (error.code === 'not-found' && (operation.type === 'UPDATE' || operation.type === 'DELETE')) {
          console.log(`⚠️ Document not found for ${operation.type} operation, removing from queue:`, operation.docId);
          // Don't re-add to queue - document doesn't exist
          continue;
        }
        
        // Re-add to queue if retries remaining
        if (operation.retries < this.retryAttempts) {
          operation.retries = (operation.retries || 0) + 1;
          this.syncQueue.push(operation);
          // Save queue with failed operation
          if (operation.userId) {
            this.saveSyncQueue(operation.userId);
          }
        } else {
          console.error(`❌ Operation failed after ${this.retryAttempts} retries, removing from queue:`, operation);
        }
      }
    }
  }

  // Execute individual sync operation (for settings sync queue - quotes no longer use sync queue)
  async executeOperation(operation) {
    const { type, collection: collectionName, docId, data, userId } = operation;

    // Quotes should not be in sync queue anymore - they save directly to Firestore
    if (collectionName === 'quotes') {
      console.warn('⚠️ Quote operation found in sync queue - this should not happen. Quotes save directly to Firestore.');
      return;
    }

    switch (type) {
      case 'CREATE':
        await addDoc(collection(db, collectionName), {
          ...data,
          userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          deviceId: this.getDeviceId()
        });
        break;
      
      case 'UPDATE':
        await updateDoc(doc(db, collectionName, docId), {
          ...data,
          updatedAt: serverTimestamp(),
          deviceId: this.getDeviceId()
        });
        break;
      
      case 'DELETE':
        await deleteDoc(doc(db, collectionName, docId));
        break;
      
      default:
        throw new Error(`Unknown operation type: ${type}`);
    }
  }

  // Set up real-time listener for quotes (user tree: users/{userId}/quotes)
  setupQuotesListener(userId, onUpdate) {
    if (this.listeners.has('quotes')) {
      this.listeners.get('quotes')(); // Unsubscribe existing listener
    }

    const quotesRef = collection(db, 'users', userId, 'quotes');
    const unsubscribe = onSnapshot(quotesRef, (snapshot) => {
      const quotes = [];
      snapshot.forEach((doc) => {
        quotes.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort by updatedAt (most recent first)
      quotes.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.() || new Date(a.updatedAt || 0);
        const bTime = b.updatedAt?.toDate?.() || new Date(b.updatedAt || 0);
        return bTime - aTime;
      });

      // Firestore is the single source of truth - just update state
      onUpdate(quotes);
    }, (error) => {
      console.error('Quotes listener error:', error);
    });

    this.listeners.set('quotes', unsubscribe);
  }

  // Set up real-time listener for user settings (window types and customer types)
  // Now uses users/{userId} collection to match iOS implementation
  setupSettingsListener(userId, onUpdate) {
    if (this.listeners.has('settings')) {
      this.listeners.get('settings')(); // Unsubscribe existing listener
    }

    console.log('🟢 [Browser] Setting up settings listener for userId:', userId);
    const userRef = doc(db, 'users', userId);
    
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (!snapshot.exists()) {
        console.log('⚠️ [Browser] User document does not exist');
        const localSettings = this.getLocalSettings(userId);
        onUpdate(localSettings);
        return;
      }

      const data = snapshot.data();
      console.log('📥 [Browser] Settings snapshot received - data keys:', Object.keys(data));

      const remoteSettings = {
        windowTypes: data.windowTypes || [],
        customerTypes: data.customerTypes || [],
        theme: data.theme || 'light', // Get theme from Firebase, default to 'light'
        lastUpdated: data.updatedAt?.toDate?.() || null
      };

      // Resolve conflicts with local settings
      const localSettings = this.getLocalSettings(userId);
      const resolvedSettings = this.resolveSettingsConflicts(localSettings, remoteSettings);
      
      onUpdate(resolvedSettings);
    }, (error) => {
      console.error('❌ [Browser] Settings listener error:', error);
      // On error, return local settings
      const localSettings = this.getLocalSettings(userId);
      onUpdate(localSettings);
    });

    this.listeners.set('settings', unsubscribe);
  }

  // Save quote directly to Firestore (simplified - no offline support)
  async saveQuote(quote, userId, isUpdate = false) {
    console.log('💾 saveQuote called:', { quoteId: quote.id, userId, isUpdate });
    
    // Ensure userId is set correctly
    const quoteUserId = userId || quote.userId;
    if (!quoteUserId) {
      throw new Error('Cannot save quote without userId');
    }
    
    // Prepare data for Firestore (match iOS fields for cross-platform parity)
    const quoteData = {
      customer: quote.customer,
      lineItems: quote.lineItems || [],
      settings: quote.settings,
      totals: quote.totals,
      userId: quoteUserId,
      updatedAt: serverTimestamp(),
      deviceId: this.getDeviceId()
    };
    if (quote.structures && quote.structures.length > 0) quoteData.structures = quote.structures;
    if (quote.notes != null) quoteData.notes = quote.notes;
    if (quote.pricesUsedPerWindowType && Object.keys(quote.pricesUsedPerWindowType).length > 0) quoteData.pricesUsedPerWindowType = quote.pricesUsedPerWindowType;
    if (quote.quoteNumber !== undefined) quoteData.quoteNumber = quote.quoteNumber;
    if (!isUpdate) quoteData.createdAt = serverTimestamp();

    const quoteId = (quote.id && quote.id.toString()) || (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) || `q_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

    if (isUpdate && quote.id) {
      // Update existing quote in Firestore (user tree)
      const quoteRef = doc(db, 'users', quoteUserId, 'quotes', quoteId);
      try {
        await updateDoc(quoteRef, quoteData);
        console.log('✅ Quote updated in Firestore');
        return { ...quote, ...quoteData, id: quoteId };
      } catch (updateError) {
        if (updateError.code === 'not-found') {
          console.log(`ℹ️ Quote ${quoteId} not found in Firestore, creating instead`);
          quoteData.id = quoteId;
          if (!quoteData.createdAt) quoteData.createdAt = serverTimestamp();
          await setDoc(doc(db, 'users', quoteUserId, 'quotes', quoteId), quoteData);
          return { ...quote, ...quoteData, id: quoteId };
        }
        throw updateError;
      }
    } else {
      // Create new quote in Firestore (user tree), client-generated ID to match iOS
      quoteData.id = quoteId;
      const quoteRef = doc(db, 'users', quoteUserId, 'quotes', quoteId);
      await setDoc(quoteRef, quoteData);
      console.log('✅ Quote created in Firestore');
      return { ...quote, ...quoteData, id: quoteId };
    }
  }

  // Delete quote directly from Firestore (user tree: users/{userId}/quotes)
  async deleteQuote(quoteId, userId) {
    const quoteIdStr = quoteId.toString();
    
    try {
      await deleteDoc(doc(db, 'users', userId, 'quotes', quoteIdStr));
      console.log(`✅ Successfully deleted quote ${quoteIdStr} from Firestore`);
    } catch (firestoreError) {
      // Handle "not-found" errors gracefully (quote already deleted)
      if (firestoreError.code === 'not-found') {
        console.log(`ℹ️ Quote ${quoteIdStr} already deleted from Firestore`);
        return; // Success - quote already deleted
      }
      
      // Re-throw other errors
      throw firestoreError;
    }
  }

  // Save user settings with real-time sync
  // Now uses users/{userId} collection to match iOS implementation
  async saveUserSettings(settings, userId) {
    console.log('💾 [Browser] Saving user settings for userId:', userId);
    const userRef = doc(db, 'users', userId);
    
    // Prepare data matching iOS structure exactly
    const windowTypesData = (settings.windowTypes || []).map(wt => ({
      id: wt.id || crypto.randomUUID(),
      name: wt.name,
      price: Number(wt.price) || 0,
      category: wt.category || 'exterior_interior'
    }));

    const customerTypesData = (settings.customerTypes || []).map(ct => ({
      id: ct.id || crypto.randomUUID(),
      name: ct.name,
      windowPrices: ct.windowPrices || {},
      windowPricesOrder: ct.windowPricesOrder || Object.keys(ct.windowPrices || {}), // Explicit order array
      usesBusinessName: ct.usesBusinessName ?? false // Feature #7: Include usesBusinessName
    }));

    console.log('💾 [Browser] Saving', windowTypesData.length, 'window types and', customerTypesData.length, 'customer types');

    const settingsData = {
      windowTypes: windowTypesData,
      customerTypes: customerTypesData,
      updatedAt: serverTimestamp()
    };

    // Include theme if provided
    if (settings.theme !== undefined) {
      settingsData.theme = settings.theme;
    }

    await setDoc(userRef, settingsData, { merge: true });

    console.log('✅ [Browser] User settings saved successfully');
  }

  // Get user settings
  // Now uses users/{userId} collection to match iOS implementation
  async getUserSettings(userId) {
    console.log('🔍 [Browser] Fetching user settings for userId:', userId);
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      console.log('📥 [Browser] User document exists - keys:', Object.keys(data));
      
      return {
        windowTypes: data.windowTypes || [],
        customerTypes: data.customerTypes || [],
        theme: data.theme || 'light', // Get theme from Firebase, default to 'light'
        lastUpdated: data.updatedAt?.toDate?.() || null
      };
    }
    
    console.log('⚠️ [Browser] User document does not exist');
    return {
      windowTypes: [],
      customerTypes: [],
      theme: 'light', // Default theme
      lastUpdated: null
    };
  }

  // Clean up all listeners
  cleanup() {
    this.listeners.forEach((unsubscribe) => unsubscribe());
    this.listeners.clear();
  }

  // Get local settings from localStorage
  getLocalSettings(userId) {
    try {
      const windowTypes = localStorage.getItem(`windowTypes_${userId}`);
      const customerTypes = localStorage.getItem(`customerTypes_${userId}`);
      const theme = localStorage.getItem('windowQuoteManager_theme');
      
      return {
        windowTypes: windowTypes ? JSON.parse(windowTypes) : [],
        customerTypes: customerTypes ? JSON.parse(customerTypes) : [],
        theme: theme || 'light',
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error loading local settings:', error);
      return {
        windowTypes: [],
        customerTypes: [],
        theme: 'light',
        lastUpdated: new Date().toISOString()
      };
    }
  }

  // Resolve conflicts between local and remote settings
  // Simplified: Firestore is single source of truth, just use remote if it exists
  resolveSettingsConflicts(localSettings, remoteSettings) {
    // If remote has data, use it (Firestore is source of truth)
    if (remoteSettings && 
        ((remoteSettings.windowTypes && remoteSettings.windowTypes.length > 0) ||
         (remoteSettings.customerTypes && remoteSettings.customerTypes.length > 0))) {
      console.log('✅ [Browser] Using remote settings from Firestore');
      return remoteSettings;
    }
    
    // If remote is empty but local has data, use local (will sync to Firestore)
    if (localSettings && 
        ((localSettings.windowTypes && localSettings.windowTypes.length > 0) ||
         (localSettings.customerTypes && localSettings.customerTypes.length > 0))) {
      console.log('ℹ️ [Browser] Remote empty, using local settings');
      return localSettings;
    }
    
    // Both empty, return empty
    return {
      windowTypes: [],
      customerTypes: [],
      theme: 'light',
      lastUpdated: null
    };
  }

  // Get sync status
  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      pendingOperations: this.syncQueue.length,
      hasActiveListeners: this.listeners.size > 0
    };
  }

  // ========== Jobber Sync Queue Stub Methods ==========
  // These are stub implementations to prevent errors - full implementation can be added later

  // Load Jobber sync queue from localStorage
  loadJobberSyncQueue(userId) {
    // Stub: do nothing, just prevent error
    try {
      const queue = localStorage.getItem(`jobberSyncQueue_${userId}`);
      this.jobberSyncQueue = queue ? JSON.parse(queue) : [];
    } catch (error) {
      this.jobberSyncQueue = [];
    }
  }

  // Process Jobber sync queue when connected
  async processJobberSyncQueue() {
    // Stub: do nothing, just prevent error
    return;
  }

  // Remove specific quote from Jobber sync queue
  removeFromJobberSyncQueue(quoteId, userId) {
    // Stub: do nothing, just prevent error
    this.jobberSyncQueue = this.jobberSyncQueue.filter(op => op.quoteId !== quoteId);
  }

  // Add quote to Jobber sync queue
  addToJobberSyncQueue(quote, userId) {
    // Stub: do nothing, just prevent error
    // Could add to queue here if needed later
  }

  // Get Jobber sync queue status
  getJobberSyncQueueStatus(userId) {
    // Stub: return empty status to prevent error
    this.loadJobberSyncQueue(userId);
    return {
      queueLength: this.jobberSyncQueue.length,
      queueItems: []
    };
  }
}

const syncService = new SyncService();
export default syncService;
